import React, { useState, useCallback } from 'react';
import { Spin, Alert } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useP9311Query } from './hooks/useP9311Query';
import QueryParams from './components/QueryParams';
import ResultTable from './components/ResultTable';
import type { QueryParams as QueryParamsType } from '../../services/p9311Api';
import './P9311Form.css';

const P9311Form: React.FC = () => {
  const { loading, result, queried, noData, runQuery, reset } = useP9311Query();

  // Filter state
  const [employeeFrom, setEmployeeFrom] = useState('');
  const [employeeTo, setEmployeeTo] = useState('');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());
  const [departmentFrom, setDepartmentFrom] = useState('');
  const [departmentTo, setDepartmentTo] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const handleQuery = useCallback(() => {
    const params: QueryParamsType = {
      employee_from: employeeFrom.trim() || undefined,
      employee_to: employeeTo.trim() || undefined,
      date_from: dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined,
      date_to: dateTo ? dateTo.format('YYYY-MM-DD') : undefined,
      department_from: departmentFrom.trim() || undefined,
      department_to: departmentTo.trim() || undefined,
      page: 1,
      size: pageSize,
    };
    setPage(1);
    runQuery(params);
  }, [employeeFrom, employeeTo, dateFrom, dateTo, departmentFrom, departmentTo, pageSize, runQuery]);

  const handlePageChange = useCallback((newPage: number, newSize: number) => {
    setPage(newPage);
    setPageSize(newSize);
    const params: QueryParamsType = {
      employee_from: employeeFrom.trim() || undefined,
      employee_to: employeeTo.trim() || undefined,
      date_from: dateFrom ? dateFrom.format('YYYY-MM-DD') : undefined,
      date_to: dateTo ? dateTo.format('YYYY-MM-DD') : undefined,
      department_from: departmentFrom.trim() || undefined,
      department_to: departmentTo.trim() || undefined,
      page: newPage,
      size: newSize,
    };
    runQuery(params);
  }, [employeeFrom, employeeTo, dateFrom, dateTo, departmentFrom, departmentTo, runQuery]);

  const handleReset = useCallback(() => {
    setEmployeeFrom('');
    setEmployeeTo('');
    setDateFrom(null);
    setDateTo(dayjs());
    setDepartmentFrom('');
    setDepartmentTo('');
    setPage(1);
    setPageSize(50);
    reset();
  }, [reset]);

  return (
    <Spin spinning={loading}>
      <div className="p9311-container max-w-6xl mx-auto p-4">
        <h2 className="text-xl font-bold mb-4">
          刷卡每筆出勤明細表 (pw9311f1)
        </h2>

        <QueryParams
          employeeFrom={employeeFrom}
          employeeTo={employeeTo}
          dateFrom={dateFrom}
          dateTo={dateTo}
          departmentFrom={departmentFrom}
          departmentTo={departmentTo}
          loading={loading}
          onEmployeeFromChange={setEmployeeFrom}
          onEmployeeToChange={setEmployeeTo}
          onDateFromChange={setDateFrom}
          onDateToChange={setDateTo}
          onDepartmentFromChange={setDepartmentFrom}
          onDepartmentToChange={setDepartmentTo}
          onQuery={handleQuery}
          onReset={handleReset}
        />

        {queried && !noData && result && (
          <ResultTable
            result={result}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
          />
        )}

        {queried && noData && (
          <Alert
            message="查無資料"
            description="查無符合條件的出勤記錄！請確認人員代號、部門代號及日期範圍是否正確。"
            type="info"
            showIcon
          />
        )}
      </div>
    </Spin>
  );
};

export default P9311Form;
